import {UserButton} from '@clerk/clerk-react'
import {Link, useLocation} from '@tanstack/react-router'
import {ChevronRight, type LucideIcon, VenetianMask, X} from 'lucide-react'
import {ContactMenuItem} from '@/components/ContactMenuItem'
import {DevUserSwitcher} from '@/components/DevUserSwitcher'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarRail,
	useSidebar,
} from '@/components/ui/sidebar'
import {formatUserName} from '@/features/permits/utils/formatters'
import {useCurrentUser} from '@/hooks/users'
import {useImpersonationStore} from '@/stores/impersonation-store'

export type SidebarNavLink = {
	to: string
	label: string
	Icon: LucideIcon
	children?: SidebarNavLink[]
}

export type SidebarNavGroup = {
	label: string
	links: SidebarNavLink[]
}

type Props = {
	navGroups: SidebarNavGroup[]
}

export function AppSidebar({navGroups}: Props) {
	const {data: currentUser} = useCurrentUser()
	const visibleGroups = navGroups.filter((group) => group.links.length > 0)

	return (
		<Sidebar collapsible='icon'>
			<SidebarHeader>
				<div className='flex items-center gap-2 px-1 py-1'>
					<div className='grid size-[22px] shrink-0 place-items-center rounded-md bg-foreground text-[11px] font-bold tracking-tight text-white'>
						R
					</div>
					<span className='truncate font-serif text-[17px] font-medium tracking-tight text-foreground group-data-[collapsible=icon]:hidden'>
						Redstack
					</span>
				</div>
			</SidebarHeader>

			<SidebarContent>
				{visibleGroups.map((group) => (
					<SidebarGroup key={group.label}>
						<SidebarGroupLabel>{group.label}</SidebarGroupLabel>
						<SidebarMenu>
							{group.links.map((link) =>
								link.children?.length ? (
									<NavCollapsible key={link.to} link={link} />
								) : (
									<NavItem key={link.to} link={link} />
								),
							)}
						</SidebarMenu>
					</SidebarGroup>
				))}
			</SidebarContent>

			{currentUser && (
				<SidebarFooter>
					<ContactMenuItem />
					<ImpersonationIndicator />
					{(currentUser.realUserRole ?? currentUser.role) === 'DEVELOPER' && (
						<DevUserSwitcher className='group-data-[collapsible=icon]:hidden' />
					)}
					{/* px-2 lines the avatar's left edge up with the nav icons above it
					    (SidebarFooter p-2 + px-2 = 16px, same as group p-2 + button p-2). */}
					<div className='flex items-center gap-2 px-2'>
						<UserButton
							// The popover portals to <body>; when the mobile sidebar (a
							// modal Radix Sheet) is open, body is pointer-events:none, so
							// taps fall straight through "Sign out". An explicit auto on
							// the card re-enables it — paired with the onInteractOutside
							// guard in ui/sidebar.tsx that keeps the sheet open.
							appearance={{
								elements: {userButtonPopoverCard: {pointerEvents: 'auto'}},
							}}
						/>
						<div className='min-w-0 leading-tight group-data-[collapsible=icon]:hidden'>
							<div className='truncate text-[12.5px] font-medium text-foreground'>
								{formatUserName(currentUser)}
							</div>
							<div className='truncate text-[10.5px] capitalize text-muted-foreground'>
								{currentUser.role.toLowerCase()}
							</div>
						</div>
					</div>
				</SidebarFooter>
			)}

			<SidebarRail />
		</Sidebar>
	)
}

function ImpersonationIndicator() {
	const {data: currentUser} = useCurrentUser()
	const setTarget = useImpersonationStore((s) => s.setTarget)

	if (!currentUser?.impersonating) return null

	function stopImpersonating() {
		setTarget(null)
		// Full reload on the BARE pathname, not cache surgery. Dropping back to
		// the org-less real developer means every mounted org-scoped query would
		// refetch without the impersonation header and 403 — and any in-page
		// reset/invalidate fires those refetches before React re-renders. The
		// search params go too: they hold org-scoped state (e.g. the board's
		// `?sprintId`) that must not leak into the next identity.
		window.location.assign(window.location.pathname)
	}

	return (
		<button
			type='button'
			onClick={stopImpersonating}
			title={`Viewing as ${formatUserName(currentUser)} — click to stop`}
			className='flex items-center gap-2 rounded-md bg-warning/10 px-2 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-warning/20'
		>
			<VenetianMask className='size-3.5 shrink-0 text-warning' />
			<span className='min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:hidden'>
				Viewing as {formatUserName(currentUser)}
			</span>
			<X className='size-3 shrink-0 text-warning group-data-[collapsible=icon]:hidden' />
		</button>
	)
}

function NavItem({link}: {link: SidebarNavLink}) {
	const {to, label, Icon} = link
	const isActive = useLocation({select: (s) => s.pathname === to})
	const {setOpenMobile} = useSidebar()

	return (
		<SidebarMenuItem>
			<SidebarMenuButton asChild isActive={isActive} tooltip={label}>
				<Link to={to} onClick={() => setOpenMobile(false)}>
					<Icon />
					<span>{label}</span>
				</Link>
			</SidebarMenuButton>
		</SidebarMenuItem>
	)
}

function NavCollapsible({link}: {link: SidebarNavLink}) {
	const {to, label, Icon, children} = link
	const isActive = useLocation({
		select: (s) =>
			s.pathname === to ||
			(children?.some((c) => s.pathname === c.to) ?? false),
	})

	return (
		<Collapsible asChild defaultOpen={isActive} className='group/collapsible'>
			<SidebarMenuItem>
				<CollapsibleTrigger asChild>
					<SidebarMenuButton tooltip={label} isActive={isActive}>
						<Icon />
						<span>{label}</span>
						<ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
					</SidebarMenuButton>
				</CollapsibleTrigger>
				<CollapsibleContent>
					<SidebarMenuSub>
						{children?.map((child) => (
							<NavSubItem key={child.to} link={child} />
						))}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	)
}

function NavSubItem({link}: {link: SidebarNavLink}) {
	const {to, label, Icon} = link
	const isActive = useLocation({select: (s) => s.pathname === to})
	const {setOpenMobile} = useSidebar()

	return (
		<SidebarMenuSubItem>
			<SidebarMenuSubButton asChild isActive={isActive}>
				<Link to={to} onClick={() => setOpenMobile(false)}>
					<Icon />
					<span>{label}</span>
				</Link>
			</SidebarMenuSubButton>
		</SidebarMenuSubItem>
	)
}
